import React, { useEffect, useRef } from 'react';
import { isValidString } from '../../common/CommonUtilsMethods';
import {
  TurnstileApi,
  TurnstileWidgetProps,
  TurnstileWindow,
} from './TurnstileWidgetInterfaces';

const TURNSTILE_SCRIPT_SRC: string =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=turnstileOnLoad';

const TURNSTILE_SITE_KEY: string = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

let turnstileScriptLoadPromise: Promise<void> | null = null;

const getTurnstileWindow = (): TurnstileWindow => {
  return window as TurnstileWindow;
};

const loadTurnstileScript = (): Promise<void> => {
  if (getTurnstileWindow().turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptLoadPromise) {
    return turnstileScriptLoadPromise;
  }

  turnstileScriptLoadPromise = new Promise<void>((resolve, reject): void => {
    const existingScript: HTMLScriptElement | null = document.querySelector(
      `script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]`
    );
    if (existingScript) {
      const checkReady = (): void => {
        if (getTurnstileWindow().turnstile) {
          resolve();
          return;
        }
        window.setTimeout(checkReady, 50);
      };
      checkReady();
      return;
    }

    const previousOnLoad: (() => void) | undefined = getTurnstileWindow().turnstileOnLoad;
    getTurnstileWindow().turnstileOnLoad = (): void => {
      if (previousOnLoad) {
        previousOnLoad();
      }
      resolve();
    };

    const scriptElement: HTMLScriptElement = document.createElement('script');
    scriptElement.src = TURNSTILE_SCRIPT_SRC;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.onerror = (): void => {
      turnstileScriptLoadPromise = null;
      reject(new Error('Failed to load Cloudflare Turnstile script'));
    };
    document.head.appendChild(scriptElement);
  });

  return turnstileScriptLoadPromise;
};

const TurnstileWidget = ({
  onTokenChange,
  resetSignal = 0,
  className = '',
}: TurnstileWidgetProps): React.JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenChangeRef = useRef<(token: string | null) => void>(onTokenChange);

  useEffect((): void => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect((): (() => void) => {
    let isCancelled: boolean = false;

    const renderWidget = async (): Promise<void> => {
      if (!isValidString(TURNSTILE_SITE_KEY)) {
        console.warn('TurnstileWidget: REACT_APP_TURNSTILE_SITE_KEY is not set');
        onTokenChangeRef.current(null);
        return;
      }

      try {
        await loadTurnstileScript();
      } catch (error: unknown) {
        console.error('TurnstileWidget: script load failed', error);
        onTokenChangeRef.current(null);
        return;
      }

      if (isCancelled || !containerRef.current) {
        return;
      }

      const turnstileApi: TurnstileApi | undefined = getTurnstileWindow().turnstile;
      if (!turnstileApi) {
        console.error('TurnstileWidget: turnstile API unavailable after script load');
        onTokenChangeRef.current(null);
        return;
      }

      if (widgetIdRef.current) {
        turnstileApi.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }

      containerRef.current.innerHTML = '';

      const renderedWidgetId: string = turnstileApi.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string): void => {
          onTokenChangeRef.current(isValidString(token) ? token : null);
        },
        'expired-callback': (): void => {
          onTokenChangeRef.current(null);
        },
        'error-callback': (): void => {
          onTokenChangeRef.current(null);
        },
        theme: 'light',
      });

      widgetIdRef.current = renderedWidgetId;
    };

    void renderWidget();

    return (): void => {
      isCancelled = true;
      const turnstileApi: TurnstileApi | undefined = getTurnstileWindow().turnstile;
      if (widgetIdRef.current && turnstileApi) {
        turnstileApi.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect((): void => {
    if (resetSignal <= 0) {
      return;
    }
    const turnstileApi: TurnstileApi | undefined = getTurnstileWindow().turnstile;
    if (!widgetIdRef.current || !turnstileApi) {
      return;
    }
    turnstileApi.reset(widgetIdRef.current);
    onTokenChangeRef.current(null);
  }, [resetSignal]);

  if (!isValidString(TURNSTILE_SITE_KEY)) {
    return (
      <div className={className} data-testid="turnstile-widget-missing-key">
        Captcha is unavailable. Please try again later.
      </div>
    );
  }

  return <div className={className} ref={containerRef} data-testid="turnstile-widget" />;
};

export { TurnstileWidget };
